import React, {
  createContext,
  forwardRef,
  HTMLAttributes,
  UIEventHandler,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

enum Direction {
  UP = "up",
  DOWN = "down",
}

const ScrollerContext = createContext(
  {} as {scrollElement?: IntersectionObserverEntry},
);

type refFunc = {
  scrollNext: () => void;
  scrollToTop: () => void;
};

export const SectionScroller = forwardRef<
  refFunc,
  HTMLAttributes<HTMLDivElement>
>(({children, onScroll, ...restProps}, fnRef) => {
  const [direction, setDirection] = useState<Direction>();
  const [scrollTop, setScrollTop] = useState<number>(0);
  const [scrollElement, setScrollElement] =
    useState<IntersectionObserverEntry>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [observedElements, setElements] = useState<
    IntersectionObserverEntry[] | null
  >(null);
  const [observer] = useState(
    new IntersectionObserver((entries) => {
      setElements(Array.from(entries));
    }),
  );

  useEffect(() => {
    if (!observedElements) return;
    if (direction === Direction.UP) {
      observedElements.forEach((ele) => {
        if (!ele.isIntersecting) {
          setScrollElement(ele);
        }
      });
      return;
    }
    const ele = observedElements.reverse().find((ele) => ele.isIntersecting);
    if (ele) setScrollElement(ele);
  }, [observedElements]);

  useEffect(() => {
    if (!containerRef.current) return;
    Array.from(containerRef.current.children).forEach((ele) =>
      observer.observe(ele),
    );
    return () => observer.disconnect();
  }, []);

  useImperativeHandle(fnRef, () => ({
    scrollToTop: () => {
      if (!containerRef.current) return;
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    scrollNext: () => {
      if (!scrollElement) return;
      scrollElement.target.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
    },
  }));

  const updateDirection: UIEventHandler<HTMLDivElement> = (e) => {
    if (onScroll) onScroll(e);
    setScrollTop((e.target as HTMLDivElement).scrollTop);
    setDirection(
      (e.target as HTMLDivElement).scrollTop > scrollTop
        ? Direction.DOWN
        : Direction.UP,
    );
  };

  return (
    <ScrollerContext.Provider value={{scrollElement}}>
      <div {...restProps} onScroll={updateDirection} ref={containerRef}>
        {children}
      </div>
    </ScrollerContext.Provider>
  );
});

SectionScroller.displayName = "SectionScroller";
